<template>
    <div>
        <form-select ref="select" v-bind="$attrs" v-model="selection" :aria-label="personBoxAriaLabel" :disabled="inputLocked" :options="options">
            <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
                <slot :name="name" v-bind="slotData" />
            </template>
        </form-select>

        <div v-if="persons.length" class="user-container-list">
            <component :is="personBoxType" v-for="id in persons" :id="id" :key="personBoxType + id" :removable="!inputLocked" @remove="remove" class="medium">
                <slot name="person-box" :id="id"/>
            </component>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {useFormSelectUser} from '@/composables/formSelectUser';
    import {usePortuna} from '@/composables/portuna';
    import FormSelect from './FormSelect.vue';
    import {ref, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'update:modelValue', input: FormUserSelectValue): void;
    }>();

    const props = withDefaults(
        defineProps<{
            options: SelectOption[] | Record<string, SelectOption>;
            modelValue: FormUserSelectValue;
            inputLocked?: boolean;
            focus?: boolean;
            removeCallbackPromise?: Nullable<(person: User) => Promise<any>>;
            useEmptyValue?: boolean;
        }>(),
        {
            removeCallbackPromise: null,
            useEmptyValue: false,
        },
    );

    const select = ref<IFormSelectContext>();

    const {
              modelValue,
              inputLocked,
              focus,
              removeCallbackPromise,
              useEmptyValue,
              options,
          } = toRefs(props);

    const {
              expose,
              selection,
              persons,
              personBoxAriaLabel,
              personBoxType,
              remove,
          } = useFormSelectUser(
        select,
        modelValue,
        inputLocked,
        focus,
        removeCallbackPromise,
        useEmptyValue,
        emit,
    );

    defineExpose(Object.assign({}, portuna, {
        componentName: 'FormSelectUser',
        expose,
    }));
</script>